export interface CheckoutRequest {
  tier: 'supporter' | 'subscriber';
  amount_dollars: number;
  /** Subscriber only; omitted means monthly. */
  interval?: 'month' | 'year';
}

export interface CheckoutResponse {
  url: string;
}

export async function createCheckout(req: CheckoutRequest): Promise<CheckoutResponse> {
  const res = await fetch('/api/checkout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (res.status === 401) {
    throw new Error('not_authenticated');
  }
  if (res.status === 409) {
    // 'already_subscribed' or 'switch_to_yearly': the page acts on the code.
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'already_subscribed');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`checkout_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as CheckoutResponse;
}

export type YearlySwitchResult =
  | { status: 'switched' }
  | { status: 'already_yearly' }
  | { status: 'redirect'; url: string };

/** Moves a live monthly subscription to yearly. Stripe charges the saved card now; PayPal always redirects for approval. */
export async function switchToYearly(): Promise<YearlySwitchResult> {
  const res = await fetch('/api/subscription/yearly', { method: 'POST', credentials: 'include' });
  if (res.status === 401) throw new Error('not_authenticated');
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`yearly_switch_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as YearlySwitchResult;
}

export interface GiftRecipient {
  twitch_user_id: string;
  login: string;
  display_name: string;
  avatar_url: string;
  is_self: boolean;
}

// Autocomplete gift recipients from the registered StreamNook user base.
export async function searchGiftRecipients(q: string): Promise<GiftRecipient[]> {
  const res = await fetch(`/api/gift/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
  if (!res.ok) return [];
  const data = (await res.json()) as { users?: Omit<GiftRecipient, 'is_self'>[] };
  return (data.users ?? []).map(u => ({ ...u, is_self: false }));
}

// Preview a gift recipient by Twitch handle. Returns null when no such user.
export async function resolveGiftRecipient(login: string): Promise<GiftRecipient | null> {
  const res = await fetch('/api/gift/resolve', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ login }),
  });
  if (res.status === 401) throw new Error('not_authenticated');
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`resolve_failed:${res.status}`);
  return (await res.json()) as GiftRecipient;
}

export async function createGiftCheckout(recipientLogin: string, termMonths: number): Promise<CheckoutResponse> {
  const res = await fetch('/api/gift/checkout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ recipient_login: recipientLogin, term_months: termMonths }),
  });
  if (res.status === 401) throw new Error('not_authenticated');
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`gift_checkout_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as CheckoutResponse;
}

// ─── PayPal ───────────────────────────────────────────────────────────────
// Same request/response shapes as their Stripe siblings above, so the pages
// can reuse the redirect code as-is.

export async function createPayPalCheckout(req: CheckoutRequest): Promise<CheckoutResponse> {
  const res = await fetch('/api/paypal/checkout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (res.status === 401) throw new Error('not_authenticated');
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? 'already_subscribed');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`paypal_checkout_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as CheckoutResponse;
}

export async function createPayPalGiftCheckout(
  recipientLogin: string,
  termMonths: number,
): Promise<CheckoutResponse> {
  const res = await fetch('/api/gift/paypal', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ recipient_login: recipientLogin, term_months: termMonths }),
  });
  if (res.status === 401) throw new Error('not_authenticated');
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`paypal_gift_checkout_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as CheckoutResponse;
}

/** PayPal has no billing portal, so cancelling is a direct call. */
export async function cancelPayPalSubscription(): Promise<{ active_through: string | null }> {
  const res = await fetch('/api/paypal/cancel', { method: 'POST', credentials: 'include' });
  if (res.status === 401) throw new Error('not_authenticated');
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`paypal_cancel_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as { active_through: string | null };
}

export async function createPortalSession(): Promise<CheckoutResponse> {
  const res = await fetch('/api/portal', { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    throw new Error(`portal_failed:${res.status}`);
  }
  return (await res.json()) as CheckoutResponse;
}

export async function equipCosmetic(slug: string | null): Promise<void> {
  const res = await fetch('/api/cosmetics/equip', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ slug }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`equip_failed:${res.status}:${detail}`);
  }
}

// Apply a profile theme ('tier' | 'paint' | atmosphere id, optionally with
// cologne '+coin'/'+border' modifiers). Server enforces the tier gates.
export async function applyProfileTheme(theme: string): Promise<void> {
  const res = await fetch('/api/cosmetics/theme', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ theme }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`theme_failed:${res.status}:${detail}`);
  }
}

// Which public-profile sections are hidden, and whether the atmosphere also
// paints behind chat messages. Either field may be sent alone.
export async function setProfilePrefs(prefs: {
  hidden_sections?: string[];
  chat_atmosphere?: boolean;
}): Promise<void> {
  const res = await fetch('/api/v1/profile/prefs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`prefs_failed:${res.status}:${detail}`);
  }
}

// ─── StreamNook Identity loadout ────────────────────────────────────────────
// The cross-client selection of which aggregated badges a member displays as
// their StreamNook presence. Read is public (by twitch user id); the write is
// owner-authenticated via the sn_session cookie (same-origin, credentials:include).
export interface Loadout {
  twitch_user_id: string;
  customized: boolean;
  badges: string[];
  paint: string | null;
  updated_at: string | null;
}

export async function getLoadout(twitchUserId: string): Promise<Loadout | null> {
  const res = await fetch(`/api/v1/identity/${encodeURIComponent(twitchUserId)}`, {
    credentials: 'include',
  });
  if (!res.ok) return null;
  return (await res.json()) as Loadout;
}

export async function setLoadout(
  badges: string[],
  paint: string | null,
  customized: boolean,
): Promise<Loadout> {
  const res = await fetch('/api/v1/identity', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ badges, paint, customized }),
  });
  if (res.status === 401) throw new Error('not_authenticated');
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`loadout_save_failed:${res.status}:${detail}`);
  }
  return (await res.json()) as Loadout;
}

export interface SiteConfig {
  stripe_publishable_key: string;
  supporter_minimum_dollars: number;
  subscriber_minimum_dollars: number;
  /** Fixed yearly Subscriber price. Absent on a deployment older than the yearly plan. */
  subscriber_yearly_dollars?: number;
  /** False until the PayPal secrets are set; hides the PayPal buttons. */
  paypal_enabled: boolean;
  /** Active seasonal theme id (src/theme/seasonal), null for none. Absent on older deployments. */
  seasonal_theme?: string | null;
}

let configCache: SiteConfig | null = null;

export async function getSiteConfig(): Promise<SiteConfig> {
  if (configCache) return configCache;
  const res = await fetch('/api/config');
  if (!res.ok) throw new Error('config_unavailable');
  configCache = (await res.json()) as SiteConfig;
  return configCache;
}
